import React, { useEffect } from 'react';
import { styled } from 'styled-components';
import { createPortal } from 'react-dom';
import { ModalTitle } from '@/styles/Typography';
import { FlexSBCBox } from '@/styles/Boxes';
import { Overlay } from '@/components/common/Overlay';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: 'small' | 'medium' | 'large' | 'custom';
  terms?: boolean;
  maxWidth?: string;
  autoHeight?: boolean;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'custom',
  terms = false,
  maxWidth = '587px',
  autoHeight = false
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // ESC 키로 모달 닫기
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      <Overlay isVisible={true} onClick={handleBackdropClick} />
      <ModalContentWrapper onMouseDown={(e) => e.stopPropagation()}>
        <ModalContent
          $size={size}
          $autoHeight={autoHeight}
          style={{ maxWidth }}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? 'modal-title' : undefined}
          aria-label={!title ? '모달' : undefined}
        >
          {title && (
            <ModalHeader>
              {!terms && (
                <div style={{ width: '24px', height: '24px' }} aria-hidden="true"></div>
              )}
              <ModalTitle id="modal-title">{title}</ModalTitle>
              <CloseButton onClick={onClose} aria-label="모달 닫기">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M18 6L6 18M6 6L18 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </CloseButton>
            </ModalHeader>
          )}

          <ModalBody>{children}</ModalBody>
        </ModalContent>
      </ModalContentWrapper>
    </>,
    document.body // ✅ createPortal의 두 번째 인자: Portal을 렌더링할 DOM 노드
  );
};

// ModalContentWrapper는 모달 컨텐츠를 중앙에 배치하는 래퍼
const ModalContentWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${({ theme }) => theme.zIndex.modal};
  padding: 20px;
  pointer-events: none;
  
  > * {
    pointer-events: auto;
  }
`;

const ModalContent = styled.div<{ $size: 'small' | 'medium' | 'large' | 'custom'; $autoHeight?: boolean }>`
  background: ${({ theme }) => theme.colors.bgWhite};
  border-radius: 20px;
  box-shadow: 0 4px 3px 0 rgba(0, 0, 0, 0.05), 6px 5px 16px 0 rgba(0, 0, 0, 0.13);
  width: ${({ $size }) => {
    switch ($size) {
      case 'small': return '400px';
      case 'medium': return '600px';
      case 'large': return '800px';
      case 'custom': return '587px';
    }
  }};
  height: ${({ $size, $autoHeight }) => $autoHeight ? 'auto' : ($size === 'custom' ? '919px' : 'auto')};
  max-width: calc(100vw - 40px);
  max-height: ${({ $size, $autoHeight }) => $autoHeight ? 'none' : ($size === 'custom' ? '919px' : '90vh')};
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 100%;
    height: auto;
    max-height: ${({ $autoHeight }) => $autoHeight ? 'none' : '90vh'};
  }
`;

const ModalHeader = styled(FlexSBCBox)`
  padding: 32px 32px 24px 32px;
  flex-shrink: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.fontGray[300]};
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  padding: 0;
  margin-top: -4px;

  &:hover {
    background: ${({ theme }) => theme.colors.bgGray[200]};
    color: ${({ theme }) => theme.colors.fontBlackText};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
  flex: 1;
  overflow-y: auto;
  color: ${({ theme }) => theme.colors.fontBlackText};
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  
  /* HTML 태그 스타일링 */
  h1, h2, h3, h4, h5, h6 {
    font-weight: 700;
    line-height: 1.3;
  }
  
  h1 { font-size: 32px; }
  h2 { font-size: 28px; }
  h3 { font-size: 24px; }
  h4 { font-size: 20px; }
  h5 { font-size: 18px; }
  h6 { font-size: 16px; }
  
  p {
    line-height: normal;
  }
  
  ul, ol {
    margin: 0 0 16px 0;
    padding-left: 24px;
    
    li {
      margin-bottom: 8px;
      line-height: 1.6;
    }
  }
  
  ol {
    list-style-type: decimal;
    
    li {
      list-style-type: decimal;
      margin-left: 8px;
    }
  }
  
  ul {
    list-style-type: disc;
    
    li {
      list-style-type: disc;
    }
  }
  
  strong, b {
    font-weight: 700;
  }
  
  em, i {
    font-style: italic;
  }
  
  a {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  blockquote {
    margin: 16px 0;
    padding: 16px;
    border-left: 4px solid ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.bgGray[100]};
  }
  
  code {
    padding: 2px 6px;
    background: ${({ theme }) => theme.colors.bgGray[200]};
    border-radius: 4px;
    font-family: monospace;
    font-size: 14px;
  }
  
  pre {
    margin: 16px 0;
    padding: 16px;
    background: ${({ theme }) => theme.colors.bgGray[200]};
    border-radius: 8px;
    overflow-x: auto;
    
    code {
      padding: 0;
      background: none;
    }
  }
  
  hr {
    margin: 24px 0;
    border: none;
    border-top: 1px solid ${({ theme }) => theme.colors.borderGray};
  }
  
  table {
    width: 100%;
    margin: 16px 0;
    border-collapse: collapse;
    
    th, td {
      padding: 12px;
      border: 1px solid ${({ theme }) => theme.colors.borderGray};
      text-align: left;
    }
    
    th {
      background: ${({ theme }) => theme.colors.bgGray[100]};
      font-weight: 600;
    }
  }
  
  /* 스크롤바 스타일링 */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.bgGray[100]};
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.borderGray};
    border-radius: 4px;
    
    &:hover {
      background: ${({ theme }) => theme.colors.fontGray[300]};
    }
  }
`;